// src/pages/finance-portal/cash-concentration/CashConcentrationReport.js
//
// Cash Concentration / FDIC Coverage report

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid,
    IconButton, LinearProgress, Skeleton, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ShieldIcon from '@mui/icons-material/Shield';
import GppBadIcon from '@mui/icons-material/GppBad';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#3b82f6';

function formatCurrency(amount) {
    if (amount == null) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function riskColor(score) {
    if (score >= 70) return '#ef4444';
    if (score >= 40) return '#f59e0b';
    return '#10b981';
}

function riskLabel(score) {
    if (score >= 70) return 'High';
    if (score >= 40) return 'Moderate';
    return 'Low';
}

export default function CashConcentrationReport() {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const fetchReport = useCallback(async (refresh = false) => {
        setError(null);
        if (!refresh) setIsLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/cash-position/concentration', {
                params: { refresh },
            });
            if (res.data?.success) setData(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load report.');
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, []);

    useEffect(() => { fetchReport(false); }, [fetchReport]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await fetchReport(true);
    };

    const totals = data?.totals || { total_cash: 0, insured: 0, uninsured: 0, institution_count: 0, max_concentration_pct: 0 };
    const institutions = data?.institutions || [];

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, fontFamily, minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: '#0f172a' }}>
                        Cash Concentration Report
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mt: 0.5 }}>
                        Distribution by institution and FDIC coverage analysis ($250K per depositor per institution).
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <Button
                        variant="outlined"
                        startIcon={isRefreshing ? <CircularProgress size={14} /> : <RestartAltIcon />}
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}
                    >
                        Recompute
                    </Button>
                    <IconButton onClick={() => fetchReport(false)}><RefreshIcon /></IconButton>
                </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Summary KPIs */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Total Cash</Typography>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: '#0f172a', mt: 0.5 }}>
                                {isLoading ? <Skeleton width={120} /> : formatCurrency(totals.total_cash)}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>
                                Across {totals.institution_count} institution{totals.institution_count !== 1 ? 's' : ''}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <ShieldIcon sx={{ color: '#10b981', fontSize: 18 }} />
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>FDIC Insured</Typography>
                            </Box>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: '#10b981', mt: 0.5 }}>
                                {isLoading ? <Skeleton width={120} /> : formatCurrency(totals.insured)}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>
                                {totals.total_cash > 0 ? ((totals.insured / totals.total_cash) * 100).toFixed(1) : 0}% covered
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <GppBadIcon sx={{ color: '#ef4444', fontSize: 18 }} />
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Uninsured Exposure</Typography>
                            </Box>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: '#ef4444', mt: 0.5 }}>
                                {isLoading ? <Skeleton width={120} /> : formatCurrency(totals.uninsured)}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>
                                Above FDIC limit
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <WarningAmberIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Max Concentration</Typography>
                            </Box>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: '#0f172a', mt: 0.5 }}>
                                {isLoading ? <Skeleton width={120} /> : `${(totals.max_concentration_pct || 0).toFixed(1)}%`}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>
                                Single-institution max
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {totals.uninsured > 0 && (
                <Alert
                    severity="warning"
                    icon={<WarningAmberIcon />}
                    sx={{ mb: 2, fontFamily }}
                >
                    {formatCurrency(totals.uninsured)} of cash sits above the $250K FDIC threshold across one or more institutions.
                    Consider diversifying via Stripe Treasury sweeps or adding additional banking partners.
                </Alert>
            )}

            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>
                            Institution Breakdown
                        </Typography>
                    </Box>
                    {isLoading ? (
                        <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={300} /></Box>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow sx={{ '& th': { fontFamily, fontWeight: 700, color: '#475569', bgcolor: '#f8fafc' } }}>
                                    <TableCell>Institution</TableCell>
                                    <TableCell align="right">Accounts</TableCell>
                                    <TableCell align="right">Total Cash</TableCell>
                                    <TableCell align="right">Insured</TableCell>
                                    <TableCell align="right">Uninsured</TableCell>
                                    <TableCell>Concentration</TableCell>
                                    <TableCell>Risk Score</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {institutions.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 5, fontFamily, color: '#94a3b8' }}>
                                            No institutions to report on.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {institutions.map((row, i) => (
                                    <TableRow key={i} hover>
                                        <TableCell sx={{ fontFamily }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <LocationCityIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem' }}>
                                                    {row.institution_name}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily }}>{row.account_count}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, color: '#0f172a' }}>
                                            {formatCurrency(row.total_cash)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily, color: '#10b981' }}>
                                            {formatCurrency(row.insured_amount)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily, color: row.uninsured_amount > 0 ? '#ef4444' : '#94a3b8', fontWeight: row.uninsured_amount > 0 ? 700 : 400 }}>
                                            {formatCurrency(row.uninsured_amount)}
                                        </TableCell>
                                        <TableCell sx={{ width: 200 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={Math.min(100, row.concentration_pct)}
                                                    sx={{
                                                        flex: 1, height: 6, borderRadius: 3, bgcolor: '#f1f5f9',
                                                        '& .MuiLinearProgress-bar': { bgcolor: ACCENT, borderRadius: 3 },
                                                    }}
                                                />
                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#475569', minWidth: 40, textAlign: 'right' }}>
                                                    {row.concentration_pct.toFixed(1)}%
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={`Score ${row.risk_score.toFixed(1)} / 100`}>
                                                <Chip
                                                    label={riskLabel(row.risk_score)}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                                        bgcolor: `${riskColor(row.risk_score)}1f`, color: riskColor(row.risk_score),
                                                    }}
                                                />
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
